/**
 * ThemeContext.js
 * 다크/라이트 모드 전환 관리
 * UI/UX 정책 기준서 반영
 */

import React, { createContext, useContext, useState } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // 기본값: 다크 모드 (정책 기준서)
  const [theme, setThemeState] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved || 'dark';
  });

  // 테마 전환 함수
  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  // 테마 직접 설정
  const setTheme = (newTheme) => {
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  // 다크 모드 여부
  const isDark = theme === 'dark';

  // 테마 색상 객체 (UI/UX 정책 기준서)
  const colors = isDark ? {
    // 다크 테마 (Onboard 스타일)
    primaryBg: '#0E0E10',
    secondaryBg: '#1C1D22',
    tertiaryBg: '#25262B',
    hoverBg: '#2F3036',
    dragHighlight: '#252542',

    primaryText: '#FFFFFF',
    secondaryText: '#E8EAED',
    tertiaryText: '#B3B8BD',
    mutedText: '#6C7178',

    primaryBorder: '#38393E',
    secondaryBorder: '#2A2B30',
    dragBorder: '#9B8AFF',

    accent: '#9B8AFF',
    accentHover: '#7B68EE',
    success: '#2DDFA8',
    successBg: 'rgba(45, 223, 168, 0.12)',
    error: '#FF6B7D',
    errorBg: 'rgba(255, 107, 125, 0.12)',
    warning: '#FFD04C',
    warningBg: 'rgba(255, 208, 76, 0.12)',
    info: '#2AE5FF',
    infoBg: 'rgba(42, 229, 255, 0.12)',

    glow: '0 0 16px rgba(155, 138, 255, 0.25)',
    gradients: {
      primary: 'linear-gradient(135deg, #7B68EE 0%, #9B8AFF 100%)',
      pink: 'linear-gradient(135deg, rgba(255,94,148,0.15) 0%, rgba(255,122,184,0.1) 100%)',
      purple: 'linear-gradient(135deg, rgba(123,104,238,0.15) 0%, rgba(155,138,255,0.1) 100%)',
    },
  } : {
    // 라이트 테마 (Onboard 스타일)
    primaryBg: '#F4F5F8',
    secondaryBg: '#FFFFFF',
    tertiaryBg: '#FFFFFF',
    hoverBg: '#EDEFF2',
    dragHighlight: '#F0ECFF',

    primaryText: '#1D1E20',
    secondaryText: '#2E3338',
    tertiaryText: '#5E6368',
    mutedText: '#9AA0A6',

    primaryBorder: '#E8EAED',
    secondaryBorder: '#DDE1E6',
    dragBorder: '#7B68EE',

    accent: '#7B68EE',
    accentHover: '#6854DB',
    success: '#1BC98E',
    successBg: 'rgba(27, 201, 142, 0.08)',
    error: '#FF4C61',
    errorBg: 'rgba(255, 76, 97, 0.08)',
    warning: '#FFB020',
    warningBg: 'rgba(255, 176, 32, 0.12)',
    info: '#00C9FF',
    infoBg: 'rgba(0, 201, 255, 0.08)',

    glow: '0 0 16px rgba(123, 104, 238, 0.25)',
    gradients: {
      primary: 'linear-gradient(135deg, #7B68EE 0%, #A594F9 100%)',
      pink: 'linear-gradient(135deg, #FFD1E3 0%, #FFDEE9 100%)',
      coral: 'linear-gradient(135deg, #FFB5A7 0%, #FFC9C0 100%)',
      yellow: 'linear-gradient(135deg, #FFF9E6 0%, #FFFAED 100%)',
    },
  };

  const value = {
    theme,
    setTheme,
    toggleTheme,
    isDark,
    colors
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;

